import { ProductTypes, type ProductType } from '../types';
import type { SelectOption } from '../types/components/common';
import { ServiceHandler } from '../utils';

export interface GetPresetsRequest {
  accountId: string;
  agencyId: string | undefined;
  effectiveDate: string | undefined;
  industryClass: number;
  isFranchisee: boolean;
  franchiseSystemConnectivity: boolean;
  naicsCode: number;
  securityQuestions?: any;
  securityAnswers?: any[];
  preset: string;
  product: ProductType;
  revenue: number;
  selectedLimit: number;
  state: string;
  yearEstablished: number;
}

export interface BasePresetMetadata {
  name: string;
  limit: number;
  deductible: number;
  retroActivePeriod?: number;
  retroActiveDate?: string;
  waitingPeriod?: number;
}

export interface CoveragePresetMetadata extends BasePresetMetadata {
  alignMax: boolean;
  isAvailable: boolean;
  isSelected: boolean;
  ordinal: number;
  refer: boolean;
  limit: number;
  deductible: number;
  isHidden: boolean;
  uuid: string;
  isMandatory: boolean;
  amendatoryCooperationPercentageLiabilityExpenses?: number;
  amendedNoticeProvisionDays?: number;
  coverageFullName?: string;
}

export interface Coverage {
  coverageDescription: string;
  coverageFullName: string;
  coverageName: string;
  uuid: string;
  preset: CoveragePresetMetadata;
  aiFactor: number;
  rules: {
    coverageName: string;
    deductibles: number[];
    isMandatory: boolean;
    limits: number[];
    hrs: number[];
    retros?: number[];
  };
}

export interface GetPresetsResponse {
  coverageGroups: {
    [coverageGroup: string]: Coverage[];
  };
  globalDeductibles: number[];
  aggPresetDeductible: number;
}

export const getPresetsByProduct = (
  productType: ProductType,
  naicsCode: number,
  presetName: string,
  payload: { data: GetPresetsRequest }
) => {
  if (productType === ProductTypes.p100_pro) {
    return getPresets(productType, naicsCode, presetName, payload);
  }

  return getPrimeXPresets(naicsCode, presetName, payload);
};

const getPrimeXPresets = ServiceHandler({
  url: '/api/rater/v1/primex/presets/rules/:naics/:presetName',
  method: 'POST',
});

export const getPresets = ServiceHandler<GetPresetsRequest, GetPresetsResponse>(
  {
    url: '/api/rater/v1/presets/rules/:productType/:naics/:presetName',
    method: 'POST',
  }
);

export const getP250Preset = ServiceHandler({
  url: '/api/rater/v1/presets/:naicsCode/:preset',
  method: 'POST',
});

export interface GetRulesGlobalsResponse {
  aggLimits: number[];
  deductibles: number[];
  hrs: number[];
  retros: number[];
}

export const getGlobalOptions = (productType: ProductType) =>
  ServiceHandler<unknown, GetRulesGlobalsResponse>({
    url:
      productType === ProductTypes.prime_tech
        ? '/api/rater/v1/primex/rules/:productType/globals/:state'
        : '/api/rater/v1/rules/:productType/globals/:state',
    method: 'POST',
  });

export type GetCustomPresetsResponse = {
  agencyId: string;
  email?: string;
  id: string;
  name: string;
  preset: string;
  presetHashMap: {
    [key: string]: PresetCoverage;
  };
}[];

export type PresetCoverage = {
  amendatoryCooperationPercentageLiabilityExpenses?: number;
  deductible?: number;
  isAvailable: boolean;
  isHidden: boolean;
  isSelected: boolean;
  limit?: number;
  name: string;
  ordinal: number;
  retroActivePeriod?: number;
  waitingPeriod?: number;
};

export const getAvailableCustomPresets = ServiceHandler<
  unknown,
  GetCustomPresetsResponse
>({
  url: '/api/rater/v1/presets/custom',
});

export interface GetRaterCarriersResponse {
  carriers: { description: string; policyPrefix: string; treatyId: string }[];
}

export const getEndorsementDelta = ServiceHandler({
  url: '/api/rater/v1/policychange/endorsement',
  method: 'POST',
});

export interface GetUWRatingOptionsResponse {
  associationCredit: {
    min: number;
    max: number;
  };
  experienceModification: {
    major: number;
    material: number;
    minimal: number;
  };
}

type GetPrimeXCancellationOptionsResponse = {
  cancellationPremiumRates: Record<string, string[]>;
  cancellationPremiumRatesMetaData: SelectOption[];
  cancellationReasonMetaData: (SelectOption & { description: string })[];
};

export const getPrimeXCancellationOptions = ServiceHandler<
  unknown,
  GetPrimeXCancellationOptionsResponse
>({
  url: '/api/rater/v1/policychange/cancellation/reasons/:productType',
  method: 'GET',
});

/**
 * Intake requirement for a supporting document on a cancellation reason.
 * Today only SINGLE_REQUIRED is consumed (Premium Financing); the BE cancel
 * DTO carries a single DocInfo, so MULTI_REQUIRED is reserved for future use.
 */
export type FileIntakeType = 'SINGLE_REQUIRED' | 'MULTI_REQUIRED';

export type CancellationReason = {
  id: string;
  key: string;
  name: string;
  description: string;
  isApplicable: boolean;
  isNocRequired: boolean;
  isCustomDateAllowed?: boolean | null;
  from?: string | null;
  to?: string | null;
  defaultCancellationDate?: string | null;
  defaultCancellationScheduledDate?: string | null;
  fileIntakeType?: FileIntakeType | null;
};

export type CancellationType = {
  id: string;
  key: string;
  name: string;
  description: string;
  cancellationReasons: CancellationReason[];
  isCustomDateAllowed: boolean | null;
  isApplicable: boolean; //If false, reason is not longer available for selection.
  from: string | null;
  to: string | null;
  defaultCancellationDate: string | null;
};

export type RaterXCancellationTypesResponse = {
  cancellationTypes: CancellationType[];
};

export const getRaterXCancellationTypes = ServiceHandler<
  unknown,
  RaterXCancellationTypesResponse
>({
  url: `/api/policy/v1/:productType/cancellation/types/:policyId`,
});
